/* ═══════════════════════════════════════════════════════════════
   The Designer — row 2: the panel buttons, the tools, the Workspace menu.

   TOOL PANEL SYSTEM v2 (docs/DESIGNER_UNIFIED_PLAN.md §2.8, Josh
   2026-08-12). Every tool panel has its own button up here, and that
   button does double duty:

     · CLICK  → the panel drops down ANCHORED beneath the button, full
                UI, portalled. Clicking anywhere outside closes it.
     · DRAG   → drop it into a dock zone to dock it, or let go outside
                every dock zone to leave it floating.

   That pair replaces the old Tools dropdown, and it is what satisfies
   Josh's menu-access-always law: no panel is ever reachable only by
   docking, because its button is always right there.

   What is left of the old menu is a **Workspace** dropdown — the
   workspace-level acts, not the tools: lock the layout, reset it,
   customise the toolbar, and publish the arrangement as the firm's
   default (§2.9, admin-gated).

   The toolbar itself keeps the Bluebeam split: a fixed CORE zone
   (undo/redo, zoom, save) that can never be unpinned, plus a
   customisable zone the user pins and unpins — right-click the row, or
   Workspace ▸ Customise toolbar…. The arrangement is per-user and
   follows them across machines (user_prefs, localStorage as the fast
   cache), same as the dock.
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { Prefs } from '../services/prefsService';

/** One control (or cluster) the toolbar can show. */
export interface ToolbarItem {
  id: string;
  /** How it is named in the Customise list and the right-click menu. */
  label: string;
  /** Core items are always shown and can never be unpinned. */
  core?: boolean;
  node: React.ReactNode;
}

/** One tool panel, as its top-bar button sees it. */
export interface PanelEntry {
  id: string;
  title: string;
  icon: React.ReactNode;
  /** Where it currently lives — drives the button's pressed state. */
  where: 'docked' | 'floating' | 'dropdown' | null;
}

export interface DesignerToolbarProps {
  items: ToolbarItem[];
  panels: PanelEntry[];
  /** Click: show this panel — anchored dropdown, or focus where it lives. */
  onPanelClick: (id: string, anchor: { left: number; bottom: number }) => void;
  /** A panel button drag began — the workspace lights its dock targets. */
  onPanelDragStart: (id: string) => void;
  /** …and ended. Outside every dock zone, the panel floats where it landed. */
  onPanelDragEnd: (id: string, x: number, y: number) => void;
  dockLocked: boolean;
  onToggleDockLocked: () => void;
  onResetLayout: () => void;
  /** Publish this arrangement as the firm's default (admin only). */
  onSetFirmDefault?: () => void;
  /** False → the item is shown but disabled, with the reason in its title,
   *  rather than hidden (DESIGN §4: nothing undiscoverable). */
  canSetFirmDefault?: boolean;
  /** Scopes the pinned set to the signed-in user. */
  userId?: string;
  /** Storage-key prefix for the pinned set. Every surface that reuses this
   *  toolbar needs its OWN prefix — sharing the Designer's would silently
   *  inherit that surface's pin customisation (an id unknown to it reads as
   *  "unpinned" instead of "not yet decided"). Defaults to the Designer's
   *  own key so the Designer itself needs no change. */
  storageKeyPrefix?: string;
}

const toolbarKey = (userId?: string, prefix = 'sf.designerToolbar.v1.') =>
  `${prefix}${userId || 'anon'}`;

/** Automated capture/QA runs sign in as a REAL user. Without this they would
    overwrite that user's cloud-synced toolbar with a throwaway one, and their
    next launch would "revert". Same guard the dock uses. */
const cloudPersistDisabled = () => {
  try {
    return localStorage.getItem('sf.dockNoCloudPersist') === '1';
  } catch {
    return false;
  }
};

/** Read the stored pin list; `null` = the user has never customised it. */
function readPins(userId?: string, prefix?: string): string[] | null {
  const key = toolbarKey(userId, prefix);
  const fromPrefs = Prefs.get(key);
  if (Array.isArray(fromPrefs)) return fromPrefs as string[];
  try {
    const raw = localStorage.getItem(key);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as string[];
    }
  } catch {
    /* private mode / corrupt — fall through to the default */
  }
  return null;
}

/* ─── The menus. Portalled, mousedown-outside + Esc to dismiss, clamped
       into the viewport — DESIGN §4 "context UI always on top". ─── */

function Menu({
  anchor,
  label,
  width = 244,
  onClose,
  children,
}: {
  anchor: { left: number; bottom: number };
  label: string;
  width?: number;
  onClose: () => void;
  children: React.ReactNode;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose]);
  React.useEffect(() => {
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
  }, []);

  const maxH = Math.min(460, window.innerHeight - anchor.bottom - 16);
  const left = Math.min(anchor.left, window.innerWidth - width - 8);
  return createPortal(
    <div
      ref={ref}
      className="dsg-menu"
      role="menu"
      aria-label={label}
      style={{ left, top: anchor.bottom + 4, width, maxHeight: maxH }}
    >
      {children}
    </div>,
    document.body
  );
}

/* ─── One tool panel's button ───
   Click opens it anchored; drag docks or floats it. Both affordances live on
   the same 32px target, and the title spells out both so nobody has to
   discover the drag. */

function PanelButton({
  panel,
  locked,
  onClick,
  onDragStart,
  onDragEnd,
}: {
  panel: PanelEntry;
  /** The workspace lock is the existing law: locked = clean work view,
   *  nothing to knock out of place. So the DRAG half of the button is off
   *  while locked, and the tooltip says why and where the switch is. The
   *  CLICK half — the access guarantee — never depends on it. */
  locked: boolean;
  onClick: (anchor: { left: number; bottom: number }) => void;
  onDragStart: () => void;
  onDragEnd: (x: number, y: number) => void;
}) {
  const open = panel.where !== null;
  return (
    <button
      type="button"
      className={`dsg-panel-btn${open ? ' dsg-tool-on' : ''}`}
      // So a surface can open a panel by id — the Studio's paper opens
      // "Section End" when the end-of-section line is clicked, the way the
      // classic canvas's onEditSpecial('{EndOfSection}') did.
      data-panel-id={panel.id}
      draggable={!locked}
      aria-pressed={open}
      aria-haspopup="dialog"
      title={
        locked
          ? `${panel.title} — click to open it here. To dock or float it, unlock the workspace first (Workspace ▸ Unlock).`
          : `${panel.title} — click to open it here, or drag it to dock or float it`
      }
      onClick={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        onClick({ left: r.left, bottom: r.bottom });
      }}
      onDragStart={(e) => {
        // The dock already accepts this exact payload from its palette tray,
        // so dragging a button into the workspace docks it with no new
        // drop-handling code (FormaDock.handleDrop).
        e.dataTransfer.setData('application/x-forma-palette', panel.id);
        e.dataTransfer.setData('text/plain', panel.title);
        e.dataTransfer.effectAllowed = 'copyMove';
        onDragStart();
      }}
      onDragEnd={(e) => onDragEnd(e.clientX, e.clientY)}
    >
      <span className="dsg-panel-btn-icon" aria-hidden="true">
        {panel.icon}
      </span>
      <span className="dsg-panel-btn-label">{panel.title}</span>
    </button>
  );
}

export function DesignerToolbar({
  items,
  panels,
  onPanelClick,
  onPanelDragStart,
  onPanelDragEnd,
  dockLocked,
  onToggleDockLocked,
  onResetLayout,
  onSetFirmDefault,
  canSetFirmDefault = false,
  userId,
  storageKeyPrefix,
}: DesignerToolbarProps) {
  const customIds = React.useMemo(() => items.filter((i) => !i.core).map((i) => i.id), [items]);

  // Default = everything pinned. A user who has never customised the toolbar
  // sees the whole set, and a control shipped later joins it automatically
  // (an unknown id is simply "not yet unpinned").
  const [pins, setPins] = React.useState<string[] | null>(() => readPins(userId, storageKeyPrefix));
  React.useEffect(() => setPins(readPins(userId, storageKeyPrefix)), [userId, storageKeyPrefix]);
  React.useEffect(
    () => Prefs.subscribe(() => setPins(readPins(userId, storageKeyPrefix))),
    [userId, storageKeyPrefix]
  );

  const isPinned = React.useCallback(
    (id: string) => (pins === null ? true : pins.includes(id)),
    [pins]
  );

  const savePins = React.useCallback(
    (next: string[]) => {
      setPins(next);
      const key = toolbarKey(userId, storageKeyPrefix);
      if (!cloudPersistDisabled()) Prefs.set(key, next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* private mode — the server copy still carries the preference */
      }
    },
    [userId, storageKeyPrefix]
  );

  const togglePin = React.useCallback(
    (id: string) => {
      const current = pins === null ? customIds : pins;
      savePins(current.includes(id) ? current.filter((x) => x !== id) : [...current, id]);
    },
    [pins, customIds, savePins]
  );

  const [wsAnchor, setWsAnchor] = React.useState<{ left: number; bottom: number } | null>(null);
  const [ctxMenu, setCtxMenu] = React.useState<{
    left: number;
    bottom: number;
    itemId?: string;
    itemLabel?: string;
  } | null>(null);
  const [customiseOpen, setCustomiseOpen] = React.useState(false);
  // Publishing a firm default changes what every colleague who has never
  // arranged their own workspace opens into. That reaches past the person
  // clicking it, so it confirms first (DESIGN §4).
  const [confirmFirm, setConfirmFirm] = React.useState(false);

  const onContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    const host = (e.target as HTMLElement).closest<HTMLElement>('[data-tb-item]');
    const id = host?.getAttribute('data-tb-item') || undefined;
    const item = id ? items.find((i) => i.id === id) : undefined;
    setCtxMenu({
      left: e.clientX,
      bottom: e.clientY,
      itemId: item && !item.core ? item.id : undefined,
      itemLabel: item?.label,
    });
  };

  const shown = items.filter((i) => i.core || isPinned(i.id));

  return (
    <div
      className="dsg-toolbar"
      role="toolbar"
      aria-label="Editor tools"
      onContextMenu={onContextMenu}
    >
      <div className="dsg-panel-bar" role="group" aria-label="Tool panels">
        {panels.map((p) => (
          <PanelButton
            key={p.id}
            panel={p}
            locked={dockLocked}
            onClick={(anchor) => onPanelClick(p.id, anchor)}
            onDragStart={() => onPanelDragStart(p.id)}
            onDragEnd={(x, y) => onPanelDragEnd(p.id, x, y)}
          />
        ))}
      </div>

      <span className="dsg-toolbar-sep" aria-hidden="true" />

      {shown.map((item) => (
        <span key={item.id} data-tb-item={item.id} className="dsg-tb-slot">
          {item.node}
        </span>
      ))}

      <button
        type="button"
        className="dsg-btn dsg-workspace-btn"
        aria-haspopup="menu"
        aria-expanded={!!wsAnchor}
        onClick={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setWsAnchor((a) => (a ? null : { left: r.left, bottom: r.bottom }));
        }}
        title="Lock, reset, and share this workspace arrangement"
      >
        Workspace
        <ChevronDown size={12} aria-hidden="true" />
      </button>

      {wsAnchor && (
        <Menu anchor={wsAnchor} label="Workspace" width={276} onClose={() => setWsAnchor(null)}>
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={!dockLocked}
            className="dsg-menu-item"
            onClick={() => {
              onToggleDockLocked();
              setWsAnchor(null);
            }}
          >
            {dockLocked ? 'Unlock to arrange panels' : 'Lock the workspace'}
          </button>
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              setCustomiseOpen(true);
              setWsAnchor(null);
            }}
          >
            Customise toolbar…
          </button>
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              onResetLayout();
              setWsAnchor(null);
            }}
            title="Put every panel back where your firm's default puts it"
          >
            Reset workspace layout
          </button>
          <div className="dsg-menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            disabled={!canSetFirmDefault}
            onClick={() => {
              setConfirmFirm(true);
              setWsAnchor(null);
            }}
            title={
              canSetFirmDefault
                ? 'Everyone who has never arranged this workspace themselves will see it like this'
                : 'Only an administrator can change what the rest of the firm sees'
            }
          >
            Set as firm default
          </button>
        </Menu>
      )}

      {ctxMenu && (
        <Menu anchor={ctxMenu} label="Toolbar" onClose={() => setCtxMenu(null)}>
          {ctxMenu.itemId && (
            <button
              type="button"
              role="menuitem"
              className="dsg-menu-item"
              onClick={() => {
                togglePin(ctxMenu.itemId!);
                setCtxMenu(null);
              }}
            >
              Remove “{ctxMenu.itemLabel}” from the toolbar
            </button>
          )}
          <button
            type="button"
            role="menuitem"
            className="dsg-menu-item"
            onClick={() => {
              setCustomiseOpen(true);
              setCtxMenu(null);
            }}
          >
            Customise toolbar…
          </button>
        </Menu>
      )}

      {confirmFirm && (
        <ConfirmDialog
          title="Set this as the firm default?"
          body="Everyone in the firm who has never arranged this workspace themselves will open into the layout you are looking at now. Anyone who has their own arrangement keeps it."
          confirmLabel="Set as firm default"
          onConfirm={() => {
            setConfirmFirm(false);
            onSetFirmDefault?.();
          }}
          onClose={() => setConfirmFirm(false)}
        />
      )}

      {customiseOpen && (
        <CustomiseDialog
          items={items}
          isPinned={isPinned}
          onToggle={togglePin}
          onResetPins={() => savePins(customIds)}
          onClose={() => setCustomiseOpen(false)}
        />
      )}
    </div>
  );
}

/* ─── Confirm a change that reaches past the person making it ─── */

function ConfirmDialog({
  title,
  body,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="dsg-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsg-modal dsg-modal-confirm" ref={ref}>
        <h2 className="dsg-modal-title">{title}</h2>
        <p className="dsg-modal-sub">{body}</p>
        <div className="dsg-desk-actions">
          <button type="button" className="dsg-btn" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="dsg-primary" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─── Customise toolbar — one checkbox row per removable control ─── */

function CustomiseDialog({
  items,
  isPinned,
  onToggle,
  onResetPins,
  onClose,
}: {
  items: ToolbarItem[];
  isPinned: (id: string) => boolean;
  onToggle: (id: string) => void;
  onResetPins: () => void;
  onClose: () => void;
}) {
  const ref = React.useRef<HTMLDivElement>(null);
  // Focus moves in, is trapped while open, and returns to whatever opened the
  // dialog (DESIGN §7).
  React.useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    ref.current?.querySelector<HTMLButtonElement>('button')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== 'Tab' || !ref.current) return;
      const focusable = [...ref.current.querySelectorAll<HTMLElement>('button:not(:disabled)')];
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      returnTo?.focus?.();
    };
  }, [onClose]);

  return createPortal(
    <div
      className="dsg-modal-scrim"
      role="dialog"
      aria-modal="true"
      aria-label="Customise toolbar"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="dsg-modal" ref={ref}>
        <h2 className="dsg-modal-title">Customise toolbar</h2>
        <p className="dsg-modal-sub">
          Pick the controls you want on the toolbar. Undo, redo, zoom and Save stay put — and every
          tool panel keeps its own button, pinned or not.
        </p>
        <div className="dsg-customise-list">
          {items.map((item) => {
            const on = isPinned(item.id);
            return (
              <button
                key={item.id}
                type="button"
                role="checkbox"
                aria-checked={item.core ? true : on}
                disabled={item.core}
                className="dsg-rsp-check"
                onClick={() => onToggle(item.id)}
              >
                <span className={`dsg-rsp-box${item.core || on ? ' is-on' : ''}`}>
                  {(item.core || on) && (
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M20 6 9 17l-5-5" />
                    </svg>
                  )}
                </span>
                <span className="dsg-rsp-check-text">
                  <span className={`dsg-rsp-check-label${on ? ' is-on' : ''}`}>{item.label}</span>
                  {item.core && <span className="dsg-rsp-check-sub">Always on the toolbar</span>}
                </span>
              </button>
            );
          })}
        </div>
        <div className="dsg-desk-actions">
          <button type="button" className="dsg-btn" onClick={onResetPins}>
            Show all
          </button>
          <button type="button" className="dsg-primary" onClick={onClose}>
            Done
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
